import React from 'react';
import { Dialog } from '../ui';
import BitmovinPlayer from '../common/BitmovinPlayer';
import HtmlResourcePreview from './HtmlResourcePreview';
import { getSafeResourceUrl } from '../../utils/getSafeResourceUrl';

export default function ClassroomResourceDrawer({ resource, onClose }) {
  if (!resource) return null;

  const { resource_name: name, resource_description: description, resource_type: type, media_url: mediaUrl } = resource;
  const isText = type === 'text';
  const isAudio = type === 'audio';
  const isUrl = type === 'url';
  const typeLabel = type === 'url' ? 'URL' : type === 'html' ? 'HTML' : `${type.charAt(0).toUpperCase()}${type.slice(1)}`;
  const url = getSafeResourceUrl(mediaUrl);

  const content = (() => {
    if (type === 'html') return <HtmlResourcePreview html={resource.html_content} title={`${name} preview`} />;
    if (!url) return <p className="tw-text-slate-500">{isUrl ? 'Invalid resource URL. Ask an administrator to update this link.' : 'This resource has no viewable content.'}</p>;
    if (type === 'image') return <img src={url} alt={name} className="tw-max-h-full tw-max-w-full tw-object-contain" />;
    if (type === 'video') return <BitmovinPlayer src={url} title={name || 'Classroom video'} objectFit="contain" className="tw-w-full" />;
    if (type === 'audio') return <audio src={url} controls preload="metadata" className="tw-w-full" />;
    if (type === 'document') return <iframe src={`${url}#toolbar=0&navpanes=0`} title={`${name} preview`} className="tw-h-full tw-w-full tw-border-0" />;
    if (isUrl) return (
      <div className="tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-3">
        <p className="tw-mb-0 tw-text-sm tw-text-slate-600">Open this website in a new tab to view it.</p>
        <a href={url} target="_blank" rel="noopener noreferrer" className="tw-inline-flex tw-rounded-lg tw-bg-brand-600 tw-px-4 tw-py-2 tw-font-medium tw-text-white hover:tw-bg-brand-700">Open website</a>
        <p className="tw-mb-0 tw-break-all tw-text-sm tw-text-slate-500">{url}</p>
      </div>
    );
    return <p className="tw-text-slate-500">This resource cannot be previewed.</p>;
  })();

  return (
    <Dialog show onHide={onClose} className={isText || isUrl ? '!tw-max-w-[35rem]' : isAudio ? '!tw-max-w-[48rem]' : '!tw-w-[calc(100%_-_2rem)] !tw-max-w-[72rem] !tw-h-[calc(100dvh_-_3rem)]'}>
      <Dialog.Header closeButton>
        <div className="tw-flex tw-w-full tw-justify-end">
          <Dialog.Title className="tw-sr-only">{name}</Dialog.Title>
          <span className="tw-rounded-full tw-border tw-border-indigo-200 tw-bg-indigo-50 tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-indigo-700">{typeLabel}</span>
        </div>
      </Dialog.Header>
      <Dialog.Body className={isText ? '!tw-p-6' : isAudio || isUrl ? 'tw-flex tw-flex-col tw-gap-4 !tw-p-6' : 'tw-flex tw-flex-col tw-gap-4 !tw-p-5'}>
        <div className="tw-min-w-0">
          <h3 className="tw-mb-2 tw-break-words tw-text-lg tw-font-semibold tw-text-slate-900">{name}</h3>
          <p className="tw-mb-0 tw-whitespace-pre-wrap tw-break-words tw-text-base tw-leading-7 tw-text-slate-700">{description || 'No description available.'}</p>
        </div>
        {!isText && (
          <>
            <div className={`tw-flex tw-min-h-0 tw-items-center tw-justify-center tw-overflow-auto tw-rounded-lg tw-bg-slate-100 ${isAudio || isUrl ? 'tw-p-4' : 'tw-min-h-[55vh] tw-flex-1'} ${['html', 'document', 'url', 'audio'].includes(type) ? '' : 'tw-p-4'}`}>
              {content}
            </div>
          </>
        )}
      </Dialog.Body>
    </Dialog>
  );
}
